import type { ReactNode } from 'react'
import type { IntlShape } from 'react-intl'

import type { Format } from '@/shared/i18n/use-format'
import type { Icon } from '@/shared/registry'

import {
  isDay,
  type FieldFilter,
  type FieldKind,
  type FilterOperator,
  type SearchState,
  type SortState,
} from './search-state'

/**
 * A filter field's or option's label: a catalog message id, or, for a name the data gives, such
 * as a custom field's (GW-29), the text itself.
 */
export type SearchLabel =
  | { readonly labelId: string; readonly label?: never }
  | { readonly label: string; readonly labelId?: never }

/** A label's text. */
export function labelText(intl: IntlShape, item: SearchLabel): string {
  return item.labelId === undefined ? item.label : intl.formatMessage({ id: item.labelId })
}

/** A field the Filters panel offers. Its id is the API's filter field. */
export type SearchFilterField = SearchLabel & {
  readonly id: string
  /**
   * `text` and `person` match text (a person by name, through Site Management's ids); `choice`
   * picks from `options`; `number` and `date` take a value or a range, and a date also a
   * relative period.
   */
  readonly kind: FieldKind
  /** Offers "is empty" and "is not empty". */
  readonly nullable?: boolean
  /** A number field that takes only 32-bit whole numbers, as a custom field's (GW-29). */
  readonly integer?: boolean
  /** A choice field's options: the API's value and its label. */
  readonly options?: readonly (SearchLabel & { readonly value: string })[]
  /** The operators offered, the default first, when the API takes fewer than its kind's, as an id field takes only equals. */
  readonly operators?: readonly FilterOperator[]
  /** For a text or person field: where the values it may take are suggested from while the viewer types. */
  readonly suggest?: FilterSuggestions
}

/**
 * Where a filter's suggestions come from: another area's list, read with `q` as its search
 * conventions take it, so it answers only with records the viewer may see on their site, active
 * ones unless the search shows inactive ones too.
 */
export interface FilterSuggestions {
  /** The list endpoint, such as `/site-management/v1/organizations`. */
  readonly path: string
  /** What reading the list needs; a viewer without it types the value with no suggestions. */
  readonly permission: string
  /** The value a record suggests, as the filter matches it; its `name` when left out. */
  readonly valueFrom?: (record: unknown) => string | null
  /** The list takes `includeInactive`, so it suggests switched-off records while the search shows them. */
  readonly hasInactive?: boolean
}

/**
 * The search text a column's cells show, marked in them: the search box's, matched anywhere, and
 * each of these filter fields', as its filter matches (`highlight.tsx`).
 */
export interface ColumnHighlight {
  readonly q?: boolean
  readonly fields?: readonly string[]
}

/** A results column. Its id is the export's column name; a custom field's is labeled by its name. */
export type SearchColumn<T> = SearchLabel & {
  readonly id: string
  /** The row's cell; `format` shows dates in the time zone the viewer chose. */
  readonly cell: (row: T, format: Format) => ReactNode
  /** Always shown: the column chooser shows it checked and fixed. */
  readonly locked?: boolean
  /** Shown until the viewer chooses columns; `true` unless set. */
  readonly shownByDefault?: boolean
  /** Its header sorts the results: by the API sort field named as the column when `true`, else by the one named. */
  readonly sortBy?: true | string
  /**
   * Marks the matched text in its cells: a cell that is plain text is marked as it is; one that is
   * a component marks its text by showing it through `Highlight`.
   */
  readonly highlight?: ColumnHighlight
}

/** The API sort field a column's header sorts by, or `undefined` when it does not sort. */
export function sortFieldOf<T>(column: SearchColumn<T>): string | undefined {
  return column.sortBy === true ? column.id : column.sortBy
}

/** The fields a screen sorts by: its columns', and its default's, which may have no column. */
export function sortFieldsOf<T>(definition: SearchDefinition<T>): readonly string[] {
  const fields = definition.columns.map((column) => sortFieldOf(column)).filter((field) => field !== undefined)
  return definition.defaultSort === undefined ? fields : [...fields, definition.defaultSort.field]
}

/** The sort fields `field` sorts together with, itself among them, or `null` on a screen that sorts by one column. */
export function sortGroupOf<T>(definition: SearchDefinition<T>, field: string): readonly string[] | null {
  return definition.sortGroups?.find((group) => group.includes(field)) ?? null
}

/**
 * One search screen: where its results come from and what it filters, sorts and shows. The list
 * endpoint follows the API's search conventions (`q`, `filter`, `sort`, `limit`, `cursor`), and,
 * when `exportable` is `true`, `{path}/export` streams the same results as CSV.
 */
export interface SearchDefinition<T> {
  /** Names the screen to saved searches, such as `site-management.users`. Never changes. */
  readonly screen: string
  /** The list endpoint, such as `/site-management/v1/users`. */
  readonly path: string
  readonly parseRow: (item: unknown) => T
  readonly rowId: (row: T) => string
  readonly filters: readonly SearchFilterField[]
  /**
   * The order before the viewer clicks a header; by default the first sortable column's,
   * ascending. Its field need not be a column's, such as when the records were created.
   */
  readonly defaultSort?: SortState
  /**
   * For a screen that sorts by several columns at once, as the mockups' Find pages do: the groups
   * of sort fields the API sorts by together. A header whose field is outside the order's group
   * starts the order afresh. Without it, the screen sorts by one column at a time.
   */
  readonly sortGroups?: readonly (readonly string[])[]
  readonly columns: readonly SearchColumn<T>[]
  /** Another feature's columns slot for this table (GW-19), each with one data call per page. */
  readonly columnSlot?: string
  /** Records per page, 25 by default. */
  readonly pageSize?: number
  /** `true` only for a list the API exports at `{path}/export`; any other offers no Export. */
  readonly exportable?: boolean
  /**
   * Message ids by API error code, for a refusal no retry fixes, such as a parent record that is
   * not found: shown in place of the search, as for no access.
   */
  readonly errorMessages?: Readonly<Record<string, string>>
  /**
   * For a list whose records can be switched off, the domain's word for them: the API lists only
   * active ones unless `includeInactive`, and the Filters panel offers "Show deactivated" (or
   * disabled, archived or deleted).
   */
  readonly inactive?: InactiveWord
}

/** What a list calls its records that are switched off. */
export type InactiveWord = 'deactivated' | 'disabled' | 'archived' | 'deleted'

/** The page around a search: its header. */
export interface SearchPageHeader {
  readonly icon: Icon
  readonly title: string
  readonly eyebrow?: string
  /** The results table's caption, when the title alone would not say what it lists; else the title. */
  readonly caption?: string
  /** Secondary actions on the header's right, such as "Create User". */
  readonly actions?: ReactNode
  /** The free-text field's placeholder, such as "Search all users...". */
  readonly searchPlaceholder: string
}

/** The operators the Filters panel offers for a field, the default first. */
export function operatorsFor(field: SearchFilterField): readonly FilterOperator[] {
  if (field.operators !== undefined) {
    return field.operators
  }
  const empty: readonly FilterOperator[] = field.nullable === true ? ['isNull', 'isNotNull'] : []
  switch (field.kind) {
    case 'text':
      return ['contains', 'notContains', 'startsWith', 'notStartsWith', 'equals', 'notEquals', ...empty]
    case 'person':
      return ['contains', 'notContains', 'startsWith', 'notStartsWith', 'equals', 'notEquals', ...empty]
    case 'choice':
      return ['in', 'notIn', ...empty]
    case 'number':
      return ['equals', 'notEquals', 'between', 'notBetween', ...empty]
    case 'date':
      return ['between', 'notBetween', 'within', 'notWithin', ...empty]
  }
}

/**
 * The columns shown, in order: the defaults, in the definition's order, until the viewer chooses;
 * then the chosen ones in the order chosen, after any locked one left out. Unknown ids are skipped.
 */
export function shownColumns<T>(
  columns: readonly SearchColumn<T>[],
  chosen: readonly string[] | null,
): readonly SearchColumn<T>[] {
  if (chosen === null) {
    const defaults = columns.filter((column) => column.locked === true || column.shownByDefault !== false)
    // A search always shows a column, even from a definition that hides them all by default.
    return defaults.length > 0 ? defaults : columns.slice(0, 1)
  }
  const picked = chosen
    .filter((id, index) => chosen.indexOf(id) === index)
    .map((id) => columns.find((column) => column.id === id))
    .filter((column): column is SearchColumn<T> => column !== undefined)
  // None of the chosen ids is a column any more, as in an old saved URL: the defaults.
  if (picked.length === 0) {
    return shownColumns(columns, null)
  }
  const locked = columns.filter((column) => column.locked === true && !picked.includes(column))
  return [...locked, ...picked]
}

/** Text the API reads as a number: digits, an optional sign and an optional decimal part. */
export function isNumber(text: string): boolean {
  return /^-?\d+(\.\d+)?$/u.test(text.trim())
}

/** Whether a number field takes `text`: a number, or for an `integer` field a 32-bit whole number. */
export function takesNumber(field: Pick<SearchFilterField, 'integer'>, text: string): boolean {
  if (field.integer !== true) {
    return isNumber(text)
  }
  const trimmed = text.trim()
  return /^-?\d+$/u.test(trimmed) && Number(trimmed) >= -(2 ** 31) && Number(trimmed) <= 2 ** 31 - 1
}

/**
 * Whether a filter fits its field: an operator the field offers (one from a hand-edited URL has
 * no editor and no meaning), a number field's values numbers, a choice field's values among its
 * options, and a date range's bounds days, which would otherwise turn into some other instant.
 */
export function fitsField(field: SearchFilterField, filter: FieldFilter): boolean {
  if (!operatorsFor(field).includes(filter.operator)) {
    return false
  }
  const values = filter.values.filter((value) => value !== '')
  if (field.kind === 'number') {
    return values.every((value) => takesNumber(field, value))
  }
  if (field.kind === 'choice') {
    // A value the field no longer offers, as in an old saved URL, which the API would refuse.
    return values.every((value) => field.options?.some((option) => option.value === value) === true)
  }
  if (field.kind === 'date' && (filter.operator === 'between' || filter.operator === 'notBetween')) {
    return values.every((value) => isDay(value))
  }
  return true
}

/**
 * The sorts a screen may use: only its sort fields, and after the first only those of the first's
 * group, or none after it on a screen that sorts by one column.
 */
function sortsFor<T>(sorts: readonly SortState[], definition: SearchDefinition<T>): readonly SortState[] {
  const fields = sortFieldsOf(definition)
  const known = sorts.filter((sort) => fields.includes(sort.field))
  const first = known[0]
  if (first === undefined) {
    return known
  }
  const group = sortGroupOf(definition, first.field)
  const kept = group === null ? [first] : known.filter((sort) => group.includes(sort.field))
  return kept.length === sorts.length ? sorts : kept
}

/**
 * The search state as a screen may use it: filters only on its own fields, sorts only by its sort
 * fields, and only its own columns. A shared or hand-edited URL can hold anything.
 */
export function forDefinition<T>(state: SearchState, definition: SearchDefinition<T>): SearchState {
  const filters = state.filters.filter((filter) => {
    const field = definition.filters.find((candidate) => candidate.id === filter.field)
    return field !== undefined && fitsField(field, filter)
  })
  const sorts = sortsFor(state.sorts, definition)
  // Only columns the screen has, so a saved search describes what is shown; none left is the defaults.
  const known = state.columns?.filter((id) => definition.columns.some((column) => column.id === id)) ?? null
  const columns = known === null || known.length === 0 ? null : known
  const sameColumns = columns === state.columns || (columns !== null && state.columns !== null && columns.length === state.columns.length)
  // Only a screen whose records can be switched off lists inactive ones.
  const includeInactive = state.includeInactive && definition.inactive !== undefined
  return filters.length === state.filters.length && sorts === state.sorts && sameColumns && includeInactive === state.includeInactive
    ? state
    : { ...state, filters, sorts, columns, includeInactive }
}
